import { useMemo, useRef, useState, type Key, type SyntheticEvent } from 'react';
import { Autocomplete, TextField, Chip, Box, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import PopoverOrSheet from '@/components/common/PopoverOrSheet';
import LockExplanation from './LockExplanation';
import { useTagLookup } from '@/hooks/useTags';
import type { LockedTag, LockReason, Tag } from '@/types';

/**
 * Pseudo-dimension for a selected slug the vocabulary no longer knows (deactivated
 * or deleted after it was applied). It gets a chip like any other tag so the admin
 * can remove it; the backend refuses to save it, so hiding it would lock the row.
 */
const UNKNOWN_DIMENSION = 'UNKNOWN';

/** Swedish labels for the three dimensions the picker groups by. */
const DIMENSION_LABEL: Record<string, string> = {
  AREA: 'Område',
  KIND: 'Dokumenttyp',
  TOPIC: 'Ämne',
  [UNKNOWN_DIMENSION]: 'Inte längre i vokabulären',
};

interface TagPickerProps {
  /** Currently selected slugs. */
  value: string[];
  onChange: (slugs: string[]) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  /**
   * Tags the farmer may not remove because a requirement in an open control
   * rests on them, with the links that require each. They render as grey,
   * non-deletable chips whose click explains why. Computed by the backend.
   */
  lockedTags?: LockedTag[];
  /**
   * Called when the farmer chooses to unlink the document from a control that
   * requires a locked tag, from the explanation panel. Resolves once the link
   * is gone; the caller refreshes `lockedTags`. Without it the panel only
   * explains. Failures are reported by the caller (toast); the picker only
   * re-enables its buttons.
   */
  onUnlinkRequest?: (slug: string, reason: LockReason) => Promise<void>;
  /** Renders the field in a compact form for dialogs. */
  size?: 'small' | 'medium';
}

/**
 * Multi-select over the tag vocabulary, grouped by dimension (AG-249).
 *
 * Grouping is the whole reason this is not a plain Autocomplete: 76 tags in one
 * flat list is unusable, while "Område / Dokumenttyp / Ämne" maps onto how the
 * farmer already thinks — a growing journal is `vaxtodling` + `journal` + what
 * it is about.
 *
 * Locked tags exist because a document linked to a requirement stays there
 * only while its tags match the requirement (AG-332). The farmer may add
 * detail but not remove what makes the link valid; the lock describes
 * requirement links, not upload presets. A click on a locked chip opens a
 * popover or sheet naming the requirements, and optionally offers to unlink
 * the document from the control instead. Editable chips are yellow, locked
 * ones grey with a lock icon. Keyboard: Autocomplete reaches the chips with
 * ArrowLeft from the input and Enter activates the focused chip; the chips
 * keep Autocomplete's tabIndex -1, because a tab stop inside the field fights
 * its own focus handling (Tab moves focus back to the input).
 */
export default function TagPicker({
  value,
  onChange,
  label = 'Taggar',
  helperText,
  error = false,
  disabled = false,
  lockedTags = [],
  onUnlinkRequest,
  size = 'medium',
}: TagPickerProps) {
  const { tags, isLoading, byDimension } = useTagLookup();

  const lockedSlugs = useMemo(() => lockedTags.map((lock) => lock.slug), [lockedTags]);
  const locked = useMemo(() => new Set(lockedSlugs), [lockedSlugs]);

  /** Slug whose explanation is open; the panel closes by itself once it leaves `lockedTags`. */
  const [explainedSlug, setExplainedSlug] = useState<string | null>(null);
  const chipAnchors = useRef(new Map<string, HTMLElement>());
  const explained = lockedTags.find((lock) => lock.slug === explainedSlug) ?? null;

  const vocabulary = useMemo(
    () => byDimension.flatMap((group) => group.tags),
    [byDimension],
  );

  /**
   * Selected slugs absent from the vocabulary. They are rendered as ordinary,
   * deletable chips (grouped under "Inte längre i vokabulären") — not restored
   * behind the admin's back — because the backend rejects them on save, and a
   * chip that cannot be removed would make the requirement impossible to edit.
   */
  const unknownSlugs = useMemo(
    () => value.filter((slug) => !vocabulary.some((tag) => tag.slug === slug)),
    [vocabulary, value],
  );
  // A locked unknown slug cannot be removed by hand, so the "remove it to save"
  // caption would mislead; it goes through the unlink in its explanation.
  const removableUnknownSlugs = useMemo(
    () => unknownSlugs.filter((slug) => !locked.has(slug)),
    [unknownSlugs, locked],
  );

  // Flatten in dimension order so Autocomplete's groupBy sees the options
  // already clustered — it groups consecutive items only. Unknown slugs come
  // last, and only while they are selected. While the vocabulary is still
  // loading every selected slug is "unknown", which doubles as the loading
  // state: the chips show the slugs and swap to display names on arrival.
  const options = useMemo<Tag[]>(
    () => [
      ...vocabulary,
      ...unknownSlugs.map((slug) => ({
        slug,
        displayName: slug,
        dimension: UNKNOWN_DIMENSION,
        sortOrder: 0,
        isActive: false,
      })),
    ],
    [vocabulary, unknownSlugs],
  );

  const selected = useMemo(
    () => options.filter((tag) => value.includes(tag.slug)),
    [options, value],
  );

  const handleChange = (_event: unknown, next: Tag[]): void => {
    const nextSlugs = next.map((tag) => tag.slug);
    // Locked slugs survive whatever the user did to the chips.
    onChange([...new Set([...lockedSlugs, ...nextSlugs])]);
  };

  const renderChip = (
    option: Tag,
    key: Key,
    chipProps: Record<string, unknown>,
  ) => {
    if (!locked.has(option.slug)) {
      return (
        <Chip
          {...chipProps}
          key={key}
          size="small"
          label={option.displayName}
          sx={{ bgcolor: 'primary.pastel' }}
        />
      );
    }
    // A click must not reach Autocomplete's input root, which toggles the listbox.
    const swallow = (event: SyntheticEvent): void => {
      event.stopPropagation();
      event.preventDefault();
    };
    return (
      <Chip
        {...chipProps}
        key={key}
        ref={(element: HTMLDivElement | null) => {
          if (element) chipAnchors.current.set(option.slug, element);
          else chipAnchors.current.delete(option.slug);
        }}
        size="small"
        label={option.displayName}
        icon={<LockOutlined />}
        onDelete={undefined}
        onMouseDown={swallow}
        onClick={(event) => {
          swallow(event);
          setExplainedSlug(option.slug);
        }}
        aria-haspopup="dialog"
        aria-label={`${option.displayName}, låst tagg – visa varför`}
        sx={{ bgcolor: 'grey.100', color: 'text.secondary' }}
      />
    );
  };

  const explainedName = explained
    ? (options.find((tag) => tag.slug === explained.slug)?.displayName ?? explained.slug)
    : '';
  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        loading={isLoading}
        disabled={disabled}
        options={options}
        value={selected}
        onChange={handleChange}
        groupBy={(option) => DIMENSION_LABEL[option.dimension] ?? option.dimension}
        getOptionLabel={(option) => option.displayName}
        isOptionEqualToValue={(option, selectedOption) => option.slug === selectedOption.slug}
        size={size}
        renderValue={(tagValue: readonly Tag[], getItemProps) =>
          tagValue.map((option, index) => {
            const { key, ...chipProps } = getItemProps({ index });
            return renderChip(option, key, chipProps);
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            placeholder={selected.length === 0 ? 'Sök eller välj taggar' : undefined}
          />
        )}
      />

      {!isLoading && removableUnknownSlugs.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Taggar som inte längre finns i vokabulären: {removableUnknownSlugs.join(', ')}. Ta bort dem
          för att kunna spara.
        </Typography>
      )}

      <PopoverOrSheet
        open={explained !== null}
        onClose={() => setExplainedSlug(null)}
        anchorEl={explained ? (chipAnchors.current.get(explained.slug) ?? null) : null}
        title={`Låst tagg: ${explainedName}`}
      >
        {explained && (
          <LockExplanation
            requiredBy={explained.requiredBy}
            subject="tag"
            onUnlink={
              onUnlinkRequest ? (reason) => onUnlinkRequest(explained.slug, reason) : undefined
            }
          />
        )}
      </PopoverOrSheet>

      {tags.length === 0 && !isLoading && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Inga taggar är upplagda ännu.
        </Typography>
      )}
    </Box>
  );
}
